/**
 * Deals a few palette colors out to the components of a logo reproduction
 * at random (api/reproduction/assignment/random). The user picks the
 * colors on the palette (laid out like the marked plate) and whether every
 * object of a source color gets the same color; components left out of
 * the marking stay left out. ``onApplied`` receives the endpoint's counts.
 */
import React, { useEffect, useState } from "react";
import {
  Alert, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Stack, Typography,
} from "@mui/material";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import axios from "axios";
import PaletteSwatchGrid from "./PaletteSwatchGrid";

export default function RandomAssignmentDialog({
  open, onClose, reproductionId, palette, columns, rasterMode = false, excludedCount = 0, onApplied,
}) {
  const [selected, setSelected] = useState([]);
  const [perSourceColor, setPerSourceColor] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);

  // A fresh pick every time the dialog opens.
  useEffect(() => {
    if (open) {
      setSelected([]);
      setPerSourceColor(true);
      setError(null);
    }
  }, [open]);

  const toggle = (entry) => setSelected((old) => (
    old.includes(entry.sample_id)
      ? old.filter((sampleId) => sampleId !== entry.sample_id)
      : [...old, entry.sample_id]
  ));

  const apply = async () => {
    setApplying(true);
    setError(null);
    try {
      const response = await axios.post("api/reproduction/assignment/random", {
        reproduction_id: reproductionId,
        sample_ids: selected,
        // A color layer is one source color anyway.
        per_source_color: rasterMode ? true : perSourceColor,
      });
      await onApplied(response.data);
      onClose();
    } catch (err) {
      console.error("Error assigning the colors at random:", err);
      setError(err?.response?.data?.error || "Assigning the colors failed.");
    } finally {
      setApplying(false);
    }
  };

  const close = () => {
    if (!applying) onClose();
  };
  const count = selected.length;

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Assign colors randomly</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Pick the palette colors to use. They are dealt out at random, each about equally often.
          </Typography>
          <PaletteSwatchGrid
            palette={palette}
            columns={columns}
            highlighted={(entry) => selected.includes(entry.sample_id)}
            onPick={toggle}
            label={(entry) => `Select palette color ${entry.index}`}
            busy={applying}
            testId="random-palette-grid"
          />
          <Typography variant="body2">
            {count === 1 ? "1 color selected" : `${count} colors selected`}
          </Typography>
          {rasterMode ? (
            <Typography variant="caption" color="text.secondary">
              Every color layer gets one color.
            </Typography>
          ) : (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={perSourceColor}
                  disabled={applying}
                  onChange={(event) => setPerSourceColor(event.target.checked)}
                />
              }
              label={<Typography variant="body2">Same color for all objects with the same source color</Typography>}
            />
          )}
          {excludedCount > 0 ? (
            <Typography variant="caption" color="text.secondary">
              {excludedCount === 1
                ? "1 component left out of the marking stays left out."
                : `${excludedCount} components left out of the marking stay left out.`}
            </Typography>
          ) : null}
          {error ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={close}
          disabled={applying}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          startIcon={applying ? <CircularProgress size={16} thickness={5} color="inherit" /> : <ShuffleIcon />}
          disabled={applying || count === 0}
          onClick={apply}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {applying ? "Assigning..." : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
